import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Upload, CheckCircle, AlertTriangle, X } from 'lucide-react'
import ExcelJS from 'exceljs'
import { format } from 'date-fns'

// ── Format definitions ──────────────────────────────────────────────────────
// Each brand's 3PL report has different column names. Add new parsers here.
const PARSERS = {
  bloomi: {
    label: 'Smart Warehouse (Bloomi)',
    detect: headers => headers.includes('Lot #') && headers.includes('Each UPC'),
    parse: row => ({
      sku:             String(row['SKU'] || '').trim(),
      lot_number:      row['Lot #'] != null ? String(row['Lot #']).trim() : null,
      expiration_date: parseExpiry(row['Expiration Date']),
      bin_location:    String(row['Location'] || '').trim(),
      quantity:        parseInt(row['Quantity']) || 0,
    }),
  },
  generic_lot_control: {
    label: 'Lot Control Report',
    detect: headers => headers.includes('warehouse_name') && headers.includes('inventory_on_hand'),
    parse: row => ({
      sku:             String(row['sku'] || '').trim(),
      lot_number:      row['lot_number'] != null ? String(row['lot_number']).trim() : null,
      expiration_date: parseExpiry(row['expiration_date']),
      bin_location:    String(row['warehouse_name'] || '').trim(),
      quantity:        parseFloat(row['inventory_on_hand']) || 0,
    }),
  },
}

function parseExpiry(val) {
  if (!val) return null
  const s = String(val).trim()
  if (!s || s === '9999-12-31' || s.startsWith('9999')) return null
  // Excel date objects
  if (val instanceof Date) {
    const y = val.getFullYear()
    if (y >= 9999) return null
    return format(val, 'yyyy-MM-dd')
  }
  // String dates like "2027-11-10"
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  return null
}

export default function LotImport() {
  const { activeBrand, profile } = useAuth()
  const [locations, setLocations] = useState([])
  const [products, setProducts] = useState([])
  const [targetLocationId, setTargetLocationId] = useState('')
  const [loading, setLoading] = useState(true)
  const [dragging, setDragging] = useState(false)
  const [parsed, setParsed] = useState(null)   // { rows, skipped, parser }
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [importHistory, setImportHistory] = useState([])
  const fileRef = useRef()

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    setParsed(null)
    setResult(null)
    setError('')
    const [{ data: locs }, { data: prods }, { data: logs }] = await Promise.all([
      supabase.from('locations').select('id,name,type').eq('brand_id', activeBrand.id).eq('is_active', true).order('name'),
      supabase.from('products').select('id,sku,name').eq('brand_id', activeBrand.id).eq('is_active', true),
      supabase.from('lot_import_log').select('*, locations(name)').eq('brand_id', activeBrand.id).order('imported_at', { ascending: false }).limit(10),
    ])
    const threePL = (locs || []).filter(l => ['1pl', '3pl'].includes(l.type))
    setLocations(threePL)
    setProducts(prods || [])
    if (threePL.length > 0) setTargetLocationId(threePL[0].id)
    setImportHistory(logs || [])
    setLoading(false)
  }

  async function handleFile(file) {
    setError('')
    setParsed(null)
    setResult(null)
    if (!file) return

    try {
      let headerNames = []
      let rowObjects = []

      const isCsv = file.name.toLowerCase().endsWith('.csv')

      if (isCsv) {
        const text = await file.text()
        const lines = text.split(/\r?\n/).filter(l => l.trim())
        if (lines.length < 2) { setError('CSV file appears empty.'); return }
        const parseCsvLine = line => {
          const cols = []
          let cur = '', inQuote = false
          for (let i = 0; i < line.length; i++) {
            const ch = line[i]
            if (ch === '"') { inQuote = !inQuote }
            else if (ch === ',' && !inQuote) { cols.push(cur.trim()); cur = '' }
            else { cur += ch }
          }
          cols.push(cur.trim())
          return cols
        }
        headerNames = parseCsvLine(lines[0]).map(h => h.replace(/^"|"$/g, '').trim())
        for (let i = 1; i < lines.length; i++) {
          const vals = parseCsvLine(lines[i])
          const rec = {}
          headerNames.forEach((h, idx) => { rec[h] = vals[idx] ?? '' })
          rowObjects.push(rec)
        }
      } else {
        const buffer = await file.arrayBuffer()
        const wb = new ExcelJS.Workbook()
        await wb.xlsx.load(buffer)
        const ws = wb.worksheets[0]

        const headerRow = ws.getRow(1)
        const headers = {}
        headerRow.eachCell((cell, col) => { headers[col] = String(cell.value || '').trim() })
        headerNames = Object.values(headers)

        ws.eachRow((row, rowNum) => {
          if (rowNum === 1) return
          const rec = {}
          Object.entries(headers).forEach(([col, name]) => { rec[name] = row.getCell(Number(col)).value })
          rowObjects.push(rec)
        })
      }

      // Detect parser
      const parser = Object.values(PARSERS).find(p => p.detect(headerNames))
      if (!parser) {
        setError('Unrecognized file format. Please upload a supported lot control report.')
        return
      }

      // Build product SKU lookup
      const skuMap = {}
      products.forEach(p => { skuMap[p.sku] = p })

      const rows = []
      const skipped = []

      rowObjects.forEach(record => {
        const parsed = parser.parse(record)
        if (!parsed.sku) return

        if (!parsed.lot_number) {
          skipped.push({ sku: parsed.sku, reason: 'blank lot #' })
          return
        }

        const product = skuMap[parsed.sku]
        if (!product) {
          if (!skipped.find(s => s.sku === parsed.sku && s.reason === 'not in catalog')) {
            skipped.push({ sku: parsed.sku, reason: 'not in catalog' })
          }
          return
        }

        rows.push({ ...parsed, product_id: product.id, product_name: product.name })
      })

      // Deduplicate by (product_id + lot_number) — matches DB unique constraint.
      // When same lot appears with different expiry dates, prefer non-null expiry; sum quantities.
      const lotMap = {}
      rows.forEach(r => {
        const key = `${r.product_id}__${r.lot_number}`
        if (!lotMap[key]) {
          lotMap[key] = { ...r, quantity: 0, bins: [] }
        }
        lotMap[key].quantity += r.quantity
        if (r.expiration_date && !lotMap[key].expiration_date) {
          lotMap[key].expiration_date = r.expiration_date
        }
        if (r.bin_location && !lotMap[key].bins.includes(r.bin_location)) {
          lotMap[key].bins.push(r.bin_location)
        }
      })
      const deduped = Object.values(lotMap).map(r => ({
        ...r,
        bin_location: r.bins.join(', '),
      }))

      setParsed({ rows: deduped, skipped, parserLabel: parser.label })
    } catch (e) {
      setError('Failed to parse file: ' + e.message)
    }
  }

  async function runImport() {
    if (!parsed || !targetLocationId) return
    setImporting(true)
    setError('')
    setResult(null)

    try {
      const now = new Date().toISOString()
      const records = parsed.rows.map(r => ({
        brand_id:        activeBrand.id,
        product_id:      r.product_id,
        location_id:     targetLocationId,
        lot_number:      r.lot_number,
        expiration_date: r.expiration_date || null,
        quantity:        r.quantity,
        bin_location:    r.bin_location || null,
        imported_at:     now,
      }))

      // Upsert on lot_number conflict, then delete stale lots not in this report
      const { error: upsertErr } = await supabase
        .from('product_lots')
        .upsert(records, { onConflict: 'brand_id,product_id,location_id,lot_number' })
      if (upsertErr) throw new Error(upsertErr.message)

      // Remove lots no longer in the report
      const activeLotNumbers = records.map(r => r.lot_number)
      const { error: delErr } = await supabase
        .from('product_lots')
        .delete()
        .eq('brand_id', activeBrand.id)
        .eq('location_id', targetLocationId)
        .not('lot_number', 'in', `(${activeLotNumbers.map(l => `"${l}"`).join(',')})`)
      if (delErr) throw new Error(delErr.message)

      await supabase.from('lot_import_log').insert({
        brand_id:       activeBrand.id,
        location_id:    targetLocationId,
        lots_imported:  records.length,
        rows_skipped:   parsed.skipped.length,
        imported_by:    (await supabase.auth.getUser()).data.user?.id,
      })

      setResult({ count: records.length, skipped: parsed.skipped.length })
      setParsed(null)
      load()
    } catch (e) {
      setError('Import failed: ' + e.message)
    }
    setImporting(false)
  }

  function onDrop(e) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  if (profile?.role !== 'super_admin' && profile?.role !== 'brand_super_admin') return <div className="empty-state"><p>Super admin access required</p></div>
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  const locationName = locations.find(l => l.id === targetLocationId)?.name || ''
  const today = new Date()

  // Expiry status helper
  function expiryStatus(expiry) {
    if (!expiry) return null
    const d = new Date(expiry + 'T12:00:00')
    const days = Math.round((d - today) / 86400000)
    if (days < 0) return { label: 'Expired', color: 'var(--danger)', badge: 'badge-danger' }
    if (days <= 90) return { label: `${days}d`, color: 'var(--warning)', badge: 'badge-warning' }
    return { label: 'OK', color: 'var(--success)', badge: 'badge-success' }
  }

  // Group preview rows by SKU
  const grouped = {}
  if (parsed) {
    parsed.rows.forEach(r => {
      if (!grouped[r.sku]) grouped[r.sku] = { name: r.product_name, lots: [] }
      grouped[r.sku].lots.push(r)
    })
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Lot Import — {activeBrand?.name}</h1>
          {importHistory.length > 0 && (
            <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>
              Last import: {format(new Date(importHistory[0].imported_at), 'MMM d, yyyy h:mm a')} — {importHistory[0].lots_imported} lots
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {error}
          <button className="btn btn-ghost btn-sm" onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}

      {result && (
        <div style={{ padding: '12px 16px', background: 'var(--success-bg)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--success)' }}>
          <CheckCircle size={16} />
          Import complete — {result.count} lots saved to {locationName}.{result.skipped > 0 ? ` ${result.skipped} rows skipped.` : ''}
        </div>
      )}

      {/* Target location picker */}
      {locations.length > 1 && (
        <div className="card">
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Target Location</div>
          <select className="select" style={{ width: '260px' }} value={targetLocationId} onChange={e => setTargetLocationId(e.target.value)}>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      )}

      {/* Upload zone */}
      {!parsed && (
        <div
          className="card"
          style={{ border: `2px dashed ${dragging ? 'var(--accent)' : 'var(--border)'}`, background: dragging ? 'var(--accent-bg)' : undefined, cursor: 'pointer', textAlign: 'center', padding: '48px 24px', transition: 'all 0.15s' }}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current.click()}
        >
          <Upload size={32} style={{ color: 'var(--text-3)', marginBottom: '12px' }} />
          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '6px' }}>Drop your 3PL lot report here</div>
          <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>or click to browse — .xlsx or .csv</div>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
        </div>
      )}

      {/* Preview */}
      {parsed && (
        <>
          {/* Summary bar */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { label: 'Lots to import', value: parsed.rows.length, color: 'var(--accent)' },
              { label: 'SKUs', value: Object.keys(grouped).length, color: 'var(--text-1)' },
              { label: 'Skipped rows', value: parsed.skipped.length, color: parsed.skipped.length > 0 ? 'var(--warning)' : 'var(--text-3)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ flex: 1, minWidth: '120px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color }}>{value}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Warning */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '12px 14px', background: 'var(--warning-bg, #fffbeb)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text-2)' }}>
            <AlertTriangle size={15} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: '1px' }} />
            This will <strong>replace all existing lot data</strong> for {locationName}. Lots no longer in the report will be removed.
          </div>

          {/* Skipped rows */}
          {parsed.skipped.length > 0 && (
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-2)' }}>Skipped ({parsed.skipped.length})</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {parsed.skipped.map((s, i) => (
                  <span key={i} style={{ fontSize: '11px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '4px', padding: '3px 8px', color: 'var(--text-3)' }}>
                    {s.sku} <span style={{ color: 'var(--text-3)', opacity: 0.6 }}>— {s.reason}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Lot preview grouped by SKU */}
          <div className="card">
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '14px' }}>Preview — {parsed.parserLabel}</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>Lot #</th>
                    <th>Expiry Date</th>
                    <th>Status</th>
                    <th>Bin</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(grouped).map(([sku, { name, lots }]) => {
                    const skuTotal = lots.reduce((s, l) => s + l.quantity, 0)
                    return lots.map((lot, i) => {
                      const status = expiryStatus(lot.expiration_date)
                      return (
                        <tr key={`${sku}-${i}`} style={{ borderTop: i === 0 ? '2px solid var(--border)' : undefined }}>
                          {i === 0 && (
                            <td rowSpan={lots.length} style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                              <span className="mono" style={{ fontSize: '12px' }}>{sku}</span>
                            </td>
                          )}
                          {i === 0 && (
                            <td rowSpan={lots.length} style={{ verticalAlign: 'top', paddingTop: '10px', fontWeight: 500, fontSize: '13px' }}>
                              {name}
                            </td>
                          )}
                          <td><span className="mono" style={{ fontSize: '12px' }}>{lot.lot_number}</span></td>
                          <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{lot.expiration_date || <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                          <td>
                            {status
                              ? <span className={`badge ${status.badge}`} style={{ fontSize: '10px' }}>{status.label}</span>
                              : <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>
                            }
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{lot.bin_location || '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '13px' }}>
                            {lot.quantity.toLocaleString()}
                            {i === lots.length - 1 && lots.length > 1 && (
                              <div style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: 400 }}>{skuTotal.toLocaleString()} total</div>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary" onClick={runImport} disabled={importing || parsed.rows.length === 0}>
              {importing ? 'Importing...' : `Import ${parsed.rows.length} lots → ${locationName}`}
            </button>
            <button className="btn btn-ghost" onClick={() => { setParsed(null); setError('') }}>
              Cancel
            </button>
          </div>
        </>
      )}

      {/* How it works */}
      {!parsed && !result && (
        <div className="card" style={{ background: 'var(--accent-bg)', borderColor: 'rgba(108,142,247,0.2)' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.7 }}>
            <strong style={{ color: 'var(--accent)' }}>How it works:</strong><br />
            Upload your 3PL lot control report (.xlsx or .csv). The app will parse each lot, match SKUs to your catalog, and replace existing lot data for {locationName || 'the selected location'}.
            Rows with blank lot numbers are skipped. SKUs not in your catalog are ignored.
            <br /><br />
            <strong style={{ color: 'var(--accent)' }}>Expiry buckets:</strong> after import, inventory will automatically show sellable, at-risk (expiring within 90 days), and expired quantities per SKU.
          </div>
        </div>
      )}

      {/* Import history */}
      {importHistory.length > 0 && (
        <div className="card">
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Import History</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Location</th><th>Lots Imported</th><th>Rows Skipped</th></tr>
              </thead>
              <tbody>
                {importHistory.map(log => (
                  <tr key={log.id}>
                    <td style={{ fontSize: '12px' }}>{format(new Date(log.imported_at), 'MMM d, yyyy h:mm a')}</td>
                    <td style={{ fontSize: '13px' }}>{log.locations?.name || '—'}</td>
                    <td style={{ fontWeight: 600, color: 'var(--success)' }}>{log.lots_imported}</td>
                    <td style={{ color: log.rows_skipped > 0 ? 'var(--warning)' : 'var(--text-3)', fontSize: '13px' }}>{log.rows_skipped || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
